import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, TextField, Grid, Card, CardContent, CardActions, Chip, 
    Dialog, DialogTitle, DialogContent, DialogActions, Paper, Select, MenuItem, FormControl, InputLabel,
    InputAdornment, ToggleButton, ToggleButtonGroup, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../../config';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import StoreIcon from '@mui/icons-material/Store';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FlagIcon from '@mui/icons-material/Flag';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import Loading from '../../components/Loading';

function ITSuppliers() {
    const [suppliers, setSuppliers] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [viewMode, setViewMode] = useState('grid');
    const [filter, setFilter] = useState('all');
    const [newSupplier, setNewSupplier] = useState({
        name: '', location: '', website: '', phone: '', comment: '', country_id: ''
    });
    const [search, setSearch] = useState('');
    const navigate = useNavigate();
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchSuppliers();
        fetchCountries();
    }, []);

    const fetchCountries = async () => {
        try {
            const res = await axios.get(`${API_URL}/setup/countries`, { headers });
            setCountries(res.data);
        } catch (e) { console.error(e); }
    };

    const fetchSuppliers = async () => {
        try {
            const res = await axios.get(`${API_URL}/it-suppliers`, { headers });
            setSuppliers(res.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const handleCreate = async () => {
        try {
            await axios.post(`${API_URL}/it-suppliers`, newSupplier, { headers });
            setOpen(false);
            setNewSupplier({ name: '', location: '', website: '', phone: '', comment: '', country_id: '' });
            fetchSuppliers();
        } catch (e) { console.error(e); }
    };

    const filtered = suppliers.filter(s => 
        s.name.toLowerCase().includes(search.toLowerCase()) ||
        (s.location && s.location.toLowerCase().includes(search.toLowerCase()))
    );

    if (loading) return <Loading />;

    return (
        <Box p={{ xs: 2, md: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, mb: 3, gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {['All Suppliers'].map(f => (
                        <Chip 
                            key={f} 
                            label={f} 
                            onClick={() => setFilter(f)} 
                            color={filter === f ? 'primary' : 'default'} 
                            variant={filter === f ? 'filled' : 'outlined'}
                            sx={{ fontWeight: 'bold' }}
                        />
                    ))}
                </Box>
            
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <TextField 
                        placeholder="Search suppliers..." 
                        size="small" 
                        value={search} 
                        onChange={e => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                            sx: { borderRadius: 5, bgcolor: 'white' }
                        }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(e, newMode) => { if (newMode) setViewMode(newMode); }}
                        size="small"
                        sx={{ bgcolor: 'white', borderRadius: 2 }}
                    >
                        <ToggleButton value="grid" aria-label="grid view">
                            <ViewModuleIcon />
                        </ToggleButton>
                        <ToggleButton value="list" aria-label="list view">
                            <ViewListIcon />
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Button 
                        variant="contained" 
                        startIcon={<AddIcon />} 
                        onClick={() => setOpen(true)}
                        sx={{ borderRadius: 5, px: 3 }}
                    >
                        New Supplier
                    </Button>                    </Box>                </Box>
            </Box>

            {viewMode === 'grid' ? (
                <Grid container spacing={3}>
                    {filtered.map(s => (
                        <Grid item xs={12} md={4} lg={3} key={s.id}>
                            <Card 
                                sx={{ height: '100%', borderRadius: 3, boxShadow: 1, cursor: 'pointer', transition: '0.3s', '&:hover': { boxShadow: 4 } }} 
                                onClick={() => navigate(`/it-suppliers/${s.id}`)}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Box sx={{ overflow: 'hidden', mr: 1 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</Typography>
                                            {s.country_name && <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>{s.country_name}</Typography>}
                                        </Box>
                                        <Chip label="Active" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }} />
                                    </Box>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                                        {s.location && (
                                            <Box display="flex" alignItems="center" color="text.secondary">
                                                <LocationOnIcon fontSize="small" sx={{ mr: 1, fontSize: 16 }} />
                                                <Typography variant="caption">{s.location}</Typography>
                                            </Box>
                                        )}
                                        {s.phone && (
                                            <Box display="flex" alignItems="center" color="text.secondary">
                                                <PhoneIcon fontSize="small" sx={{ mr: 1, fontSize: 16 }} />
                                                <Typography variant="caption">{s.phone}</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                    {filtered.length === 0 && (
                        <Grid item xs={12}>
                            <Typography align="center" sx={{ py: 3, color: 'text.secondary' }}>No suppliers found</Typography>
                        </Grid>
                    )}
                </Grid>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 1 }}>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Country</TableCell>
                                <TableCell>Location</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Website</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.map((s) => (
                                <TableRow key={s.id} hover onClick={() => navigate(`/it-suppliers/${s.id}`)} sx={{ cursor: 'pointer' }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{s.name}</TableCell>
                                    <TableCell>{s.country_name || '-'}</TableCell>
                                    <TableCell>{s.location || '-'}</TableCell>
                                    <TableCell>{s.phone || '-'}</TableCell>
                                    <TableCell>
                                        {s.website ? (
                                            <a href={s.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                                link
                                            </a>
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell>
                                        <Button size="small" endIcon={<ArrowForwardIcon />}>View</Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>No suppliers found</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Create Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle>New IT Supplier</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <TextField 
                            label="Name" 
                            fullWidth 
                            variant="outlined"
                            value={newSupplier.name} 
                            onChange={(e) => setNewSupplier({...newSupplier, name: e.target.value})} 
                        />
                        <FormControl fullWidth>
                            <InputLabel>Country</InputLabel>
                            <Select
                                value={newSupplier.country_id || ''}
                                label="Country"
                                onChange={(e) => setNewSupplier({ ...newSupplier, country_id: e.target.value })}
                            >
                                <MenuItem value=""><em>None</em></MenuItem>
                                {countries.map(c => (
                                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField 
                            label="Location" 
                            fullWidth 
                            variant="outlined"
                            value={newSupplier.location} 
                            onChange={(e) => setNewSupplier({...newSupplier, location: e.target.value})} 
                        />
                        <TextField 
                            label="Website" 
                            fullWidth 
                            variant="outlined"
                            value={newSupplier.website} 
                            onChange={(e) => setNewSupplier({...newSupplier, website: e.target.value})} 
                        />
                        <TextField 
                            label="Phone" 
                            fullWidth 
                            variant="outlined"
                            value={newSupplier.phone} 
                            onChange={(e) => setNewSupplier({...newSupplier, phone: e.target.value})} 
                        />
                        <TextField 
                            label="Comment" 
                            fullWidth 
                            multiline
                            rows={3}
                            variant="outlined"
                            value={newSupplier.comment} 
                            onChange={(e) => setNewSupplier({...newSupplier, comment: e.target.value})} 
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreate} variant="contained" disabled={!newSupplier.name}>Create Supplier</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default ITSuppliers;
